import React, { useState, useEffect, useCallback } from 'react'
import { GET_FUNDING_REQUEST_BY_STATUS } from '../../../components/graphql/queries'
import type { FundingRequestSum, PopupPropsFR } from '../../Ops/utils/types'
import { useQueryWithExtendedTime } from '../../../hooks/useQueryWithExtendedTime'
import Card from '../../../components/card'
import FundingRequestCard from '../../Ops/funding-request/FundingRequestCard'
import Message from '../../../components/message'
import PopupScreen from '../../Cap-pc/pop-up'
import GenericDropdown from '../../../components/dropdown'

const GroupedFundingRequest = () => {
  const today = new Date()
  const [filters, setFilters] = useState({
    selectedTradeDate: 'Today',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  })

  const [selectedIssuers, setSelectedIssuers] = useState<string[]>([])
  const [selectedCpPrograms, setSelectedCpPrograms] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [messageColor, setMessageColor] = useState<'red' | 'green' | 'yellow'>('green')
  const [expandedStatuses, setExpandedStatuses] = useState<Record<string, boolean>>({})

  const [groupedData, setGroupedData] = useState<Record<string, FundingRequestSum[]>>({})
  const [issuerOptions, setIssuerOptions] = useState<string[]>([])
  const [cpProgramOptions, setCpProgramOptions] = useState<string[]>([])
  const [statusOptions, setStatusOptions] = useState<string[]>([])

  const tradeDateMenu = ['Today', 'Past 3 Days', 'Past 7 Days', 'Past 30 Days', 'All']

  useEffect(() => {
    if (!filters.selectedTradeDate) return

    if (filters.selectedTradeDate === 'All') {
      setFilters(prev => ({
        ...prev,
        fromDate: '',
        toDate: ''
      }))
      return
    }
    const newFromDate = new Date(today)
    switch (filters.selectedTradeDate) {
      case 'Today':
        newFromDate.setDate(today.getDate())
        break
      case 'Past 3 Days':
        newFromDate.setDate(today.getDate() - 3)
        break
      case 'Past 7 Days':
        newFromDate.setDate(today.getDate() - 7)
        break
      case 'Past 30 Days':
        newFromDate.setDate(today.getDate() - 30)
        break
      default:
        newFromDate.setDate(today.getDate())
    }

    setFilters((prev) => ({
      ...prev,
      fromDate: newFromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    }))
  }, [filters.selectedTradeDate])

  const { data, QueryWithExtendedTimeComponent, loading, error, refetch } = useQueryWithExtendedTime({
    query: GET_FUNDING_REQUEST_BY_STATUS,
    options: {
      variables: {
        status: null,
        from_date: filters.fromDate || '2024-01-01',
        to_date: filters.toDate || new Date().toISOString().split('T')[0]
      }
    }
  })

  useEffect(() => {
    if (data?.getFundingRequestByStatus) {
      const requests = data.getFundingRequestByStatus as FundingRequestSum[]
      const grouped = groupByStatus(requests)
      setGroupedData(grouped)

      const initialExpandedStatuses: Record<string, boolean> = {}
      Object.keys(grouped).forEach(status => {
        initialExpandedStatuses[status] = false
      })
      setExpandedStatuses(initialExpandedStatuses)

      const uniqueIssuers = Array.from(
        new Set(
          requests
            .map(item => item.issuerName)
            .filter((name): name is string => name !== null && name !== undefined)
        )
      )
      setIssuerOptions(['All Issuer', ...uniqueIssuers])

      const uniqueCpPrograms = Array.from(
        new Set(
          requests
            .map(item => item.counterpartyName)
            .filter((name): name is string => name !== null && name !== undefined)
        )
      )
      setCpProgramOptions(['All Counterparty', ...uniqueCpPrograms])

      const allStatuses = new Set<string>()
      requests.forEach(item => {
        if (item.assetStatus === 'ASSET-NOT-ASSIGNED') {
          allStatuses.add(`${item.assetStatus}:${item.loanStatus ?? 'NEW'}`)
          allStatuses.add(item.assetStatus)
        } else {
          allStatuses.add(item.assetStatus)
        }
      })

      const statusOpts = Array.from(allStatuses).sort((a, b) => a.localeCompare(b))
      setStatusOptions(['All Status', ...statusOpts])
    }
  }, [data])

  const groupByStatus = (list: FundingRequestSum[]): Record<string, FundingRequestSum[]> => {
    return list.reduce<Record<string, FundingRequestSum[]>>((acc, item) => {
      const statusKey = item.assetStatus === 'ASSET-NOT-ASSIGNED'
        ? `${item.assetStatus}:${item.loanStatus ?? 'NEW'}`
        : item.assetStatus

      if (!acc[statusKey]) {
        acc[statusKey] = []
      }
      acc[statusKey].unshift(item)
      return acc
    }, {})
  }

  const filterFundingRequests = (requests: FundingRequestSum[]) => {
    return requests.filter(request => {
      const requestDate = new Date(request.timeSubmitted).toISOString().split('T')[0]
      const matchesDate = !filters.fromDate || requestDate >= filters.fromDate
      const matchesEndDate = !filters.toDate || requestDate <= filters.toDate

      const matchesIssuer = selectedIssuers.length === 0 ||
        selectedIssuers[0] === 'All Issuer' ||
        (request.issuerName && selectedIssuers.includes(request.issuerName))

      const matchesCpProgram = request.assetStatus === 'ASSET-NOT-ASSIGNED' ||
        selectedCpPrograms.length === 0 ||
        selectedCpPrograms[0] === 'All Counterparty' ||
        (request.counterpartyName && selectedCpPrograms.includes(request.counterpartyName))

      const requestStatusKey = request.assetStatus === 'ASSET-NOT-ASSIGNED'
        ? `${request.assetStatus}:${request.loanStatus ?? 'NEW'}`
        : request.assetStatus

      const matchesStatus = selectedStatuses.length === 0 ||
        selectedStatuses[0] === 'All Status' ||
        selectedStatuses.some(selectedStatus => {
          if (selectedStatus === 'ASSET-NOT-ASSIGNED') {
            return request.assetStatus === 'ASSET-NOT-ASSIGNED'
          }
          return requestStatusKey === selectedStatus
        })

      return matchesDate && matchesEndDate && matchesIssuer && matchesCpProgram && matchesStatus
    })
  }

  const handleTradeDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    setFilters(prev => ({ ...prev, selectedTradeDate: selectedValue }))
  }

  const handleIssuerChange = (value: string) => {
    setSelectedIssuers(value === 'All Issuer' ? [] : [value])
  }

  const handleCpProgramChange = (value: string) => {
    setSelectedCpPrograms(value === 'All Counterparty' ? [] : [value])
  }

  const handleStatusChange = (value: string) => {
    setSelectedStatuses(value === 'All Status' ? [] : [value])
  }

  const resetAllFilters = () => {
    setFilters({
      selectedTradeDate: 'Today',
      fromDate: today.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    })
    setSelectedIssuers([])
    setSelectedCpPrograms([])
    setSelectedStatuses([])
  }

  const toggleExpandStatus = (statusKey: string) => {
    setExpandedStatuses(prev => ({
      ...prev,
      [statusKey]: !prev[statusKey]
    }))
  }

  const renderPopupComponent = useCallback(
    (fundingRequestId: number, frOnly: boolean, popupProps: PopupPropsFR) => {
      return (
        <PopupScreen
          fundingRequestADCId={fundingRequestId}
          stage="read-only"
          setMessage={setMessage}
          setMessageColor={setMessageColor}
          {...popupProps}
          frOnly={frOnly}
        />
      )
    },
    [setMessage, setMessageColor]
  )

  if (loading || error) return <QueryWithExtendedTimeComponent />

  const statusGroups = Object.keys(groupedData).sort((a, b) => {
    if (a.startsWith('ASSET-NOT-ASSIGNED') && !b.startsWith('ASSET-NOT-ASSIGNED')) return -1
    if (!a.startsWith('ASSET-NOT-ASSIGNED') && b.startsWith('ASSET-NOT-ASSIGNED')) return 1
    return a.localeCompare(b)
  })

  return (
    <>
      <div className="flex flex-wrap items-center w-full p-4 bg-gray-100 rounded-lg mb-4 gap-2">
        <select
          value={filters.selectedTradeDate}
          onChange={handleTradeDateChange}
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        >
          {tradeDateMenu.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <GenericDropdown
          options={issuerOptions}
          selectedValue={selectedIssuers[0] || 'All Issuer'}
          onChange={handleIssuerChange}
          displayMsg="Select Issuer"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        />

        <GenericDropdown
          options={cpProgramOptions}
          selectedValue={selectedCpPrograms[0] || 'All Counterparty'}
          onChange={handleCpProgramChange}
          displayMsg="Select Counterparty"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        />

        <GenericDropdown
          options={statusOptions}
          selectedValue={selectedStatuses[0] || 'All Status'}
          onChange={handleStatusChange}
          displayMsg="Select Status"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        />

        <button
          onClick={resetAllFilters}
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 ml-auto"
        >
          Reset to All
        </button>
      </div>
      {message && (
          <Message
            message={message}
            color={messageColor}
            clearMessage={() => { setMessage(null) }}
          />
      )}
      <div className="p-4">
        {statusGroups.map(statusKey => {
          const requests = groupedData[statusKey]
          if (!requests) return null

          const filteredRequests = filterFundingRequests(requests)
          if (filteredRequests.length === 0) return null

          const isExpanded = expandedStatuses[statusKey] || false
          const displayRequests = isExpanded
            ? filteredRequests
            : filteredRequests.slice(0, 6)

          return (
            <div key={statusKey} className="mb-8">
              <div className="flex items-center mb-4">
                <h2 className="text-lg font-bold">
                  {statusKey.replace(':', ' - ')} ({filteredRequests.length})
                </h2>
                {filteredRequests.length > 6 && (
                  <button
                    onClick={() => { toggleExpandStatus(statusKey) }}
                    className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {isExpanded ? 'Show Less' : 'Show All'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap justify-begin gap-4">
                {displayRequests
                  .sort((a, b) => new Date(b.timeSubmitted).getTime() - new Date(a.timeSubmitted).getTime())
                  .map((fundingRequest) => (
                    <Card
                      key={fundingRequest.fundingRequestADCId}
                      refetch={refetch}
                      CardBodyComponent={(cardBodyProps) => (
                        <FundingRequestCard
                          data={fundingRequest}
                          {...cardBodyProps}
                          status={fundingRequest.assetStatus}
                          hasAsset={fundingRequest.assetStatus !== 'ASSET-NOT-ASSIGNED'}
                        />
                      )}
                      PopupComponent={(popupProps) => renderPopupComponent(
                        fundingRequest.fundingRequestADCId,
                        fundingRequest.assetStatus === 'ASSET-NOT-ASSIGNED',
                        { ...popupProps }
                      )}
                    />
                  ))}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default GroupedFundingRequest
